import { useState, useEffect } from 'react';
import { Share2, Download, X, Smartphone, Check, Apple, ChevronRight } from 'lucide-react';

/**
 * PWAInstallPrompt - Prompts users to install Par Seeker as a PWA
 * Shows share target benefits and installation instructions
 * 
 * Variants:
 * - 'banner' (default): Full banner with benefits list
 * - 'compact': Smaller button for login page
 * - 'ios-shortcut': iOS Shortcut guide for sharing
 */
const PWAInstallPrompt = ({ onDismiss, variant = 'banner' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showIOSShortcutGuide, setShowIOSShortcutGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for beforeinstallprompt (Android/Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Chrome - use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      // iOS - show instructions
      setShowInstructions(true);
    }
  };

  // Compact variant for login page
  if (variant === 'compact') {
    if (isInstalled) return null;
    
    return (
      <button
        onClick={handleInstall}
        className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm hover:bg-white/20 transition-colors"
        data-testid="pwa-install-compact"
      >
        <Download className="w-4 h-4" />
        <span>Install App</span>
      </button>
    );
  }

  // iOS Shortcut Guide variant
  if (variant === 'ios-shortcut' || showIOSShortcutGuide) {
    return (
      <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Apple className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-[var(--ps-text-primary)]">iOS Quick Share Setup</h3>
                <p className="text-sm text-[var(--ps-text-muted)]">Share videos directly from any app</p>
              </div>
            </div>
            {onDismiss && (
              <button onClick={onDismiss} className="text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-[var(--ps-text-secondary)]">
            Since iOS doesn&apos;t support direct PWA sharing, here&apos;s how to share videos quickly:
          </p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-[var(--ps-bg-elevated)] rounded-lg">
              <span className="w-6 h-6 rounded-full bg-[var(--ps-primary)] text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">1</span>
              <div>
                <p className="text-sm font-medium text-[var(--ps-text-primary)]">In TikTok/YouTube/Instagram</p>
                <p className="text-xs text-[var(--ps-text-muted)]">Tap Share → Copy Link</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-[var(--ps-bg-elevated)] rounded-lg">
              <span className="w-6 h-6 rounded-full bg-[var(--ps-primary)] text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">2</span>
              <div>
                <p className="text-sm font-medium text-[var(--ps-text-primary)]">Open Par Seeker</p>
                <p className="text-xs text-[var(--ps-text-muted)]">From your home screen</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-[var(--ps-bg-elevated)] rounded-lg">
              <span className="w-6 h-6 rounded-full bg-[var(--ps-primary)] text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">3</span>
              <div>
                <p className="text-sm font-medium text-[var(--ps-text-primary)]">Create Post & Paste</p>
                <p className="text-xs text-[var(--ps-text-muted)]">The video will auto-embed!</p>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-[var(--ps-border)]">
            <p className="text-xs text-[var(--ps-text-muted)] flex items-center gap-1">
              <span className="text-yellow-500">💡</span> Pro tip: Long-press &quot;Copy Link&quot; for faster access
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isInstalled) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <Check className="w-5 h-5 text-green-500" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-green-400">Par Seeker Installed!</p>
            <p className="text-sm text-[var(--ps-text-muted)]">
              {isIOS ? 'Copy & paste video links to share' : 'You can now share content from any app'}
            </p>
          </div>
          {isIOS && (
            <button 
              onClick={() => setShowIOSShortcutGuide(true)}
              className="text-sm text-[var(--ps-primary)] hover:underline flex items-center gap-1"
            >
              How to share <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[var(--ps-primary)]/20 to-transparent p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[var(--ps-primary)] flex items-center justify-center">
              <Share2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[var(--ps-text-primary)]">
                {isIOS ? 'Add Par Seeker to Home Screen' : 'Share from Any App!'}
              </h3>
              <p className="text-sm text-[var(--ps-text-muted)]">
                {isIOS ? 'Quick access & offline support' : 'Install Par Seeker for easy sharing'}
              </p>
            </div>
          </div>
          {onDismiss && (
            <button onClick={onDismiss} className="text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Benefits */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--ps-primary)]/10 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-4 h-4 text-[var(--ps-primary)]" />
          </div>
          <p className="text-sm text-[var(--ps-text-secondary)]">
            {isIOS 
              ? 'Works offline, loads fast, feels like a native app'
              : 'Share TikTok, Instagram, YouTube videos directly to your feed'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--ps-primary)]/10 flex items-center justify-center flex-shrink-0">
            <Download className="w-4 h-4 text-[var(--ps-primary)]" />
          </div>
          <p className="text-sm text-[var(--ps-text-secondary)]">
            {isIOS
              ? 'Home screen icon for quick access'
              : 'Works offline, fast loading, feels like a native app'
            }
          </p>
        </div>
      </div>

      {/* iOS Instructions Modal */}
      {showInstructions && (
        <div className="p-4 bg-[var(--ps-bg-elevated)] border-t border-[var(--ps-border)]">
          <p className="font-medium text-[var(--ps-text-primary)] mb-3">To install on iOS:</p>
          <ol className="text-sm text-[var(--ps-text-secondary)] space-y-2">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[var(--ps-primary)] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
              <span>Tap the <strong>Share button</strong> at the bottom of Safari (square with arrow)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[var(--ps-primary)] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
              <span>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-[var(--ps-primary)] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
              <span>Tap <strong>&quot;Add&quot;</strong> in the top right</span>
            </li>
          </ol>
          <button
            onClick={() => setShowInstructions(false)}
            className="w-full mt-4 px-4 py-2 bg-[var(--ps-primary)] text-white rounded-lg font-medium"
          >
            Got it!
          </button>
        </div>
      )}

      {/* Install Button */}
      {!showInstructions && (
        <div className="p-4 border-t border-[var(--ps-border)]">
          <button
            onClick={handleInstall}
            className="w-full px-4 py-3 bg-[var(--ps-primary)] text-white rounded-xl font-medium hover:bg-[var(--ps-primary-hover)] transition-colors flex items-center justify-center gap-2"
            data-testid="pwa-install-btn"
          >
            <Download className="w-5 h-5" />
            {isIOS ? 'How to Install' : 'Install Par Seeker'}
          </button>
          
          {/* iOS sharing tip */}
          {isIOS && (
            <button
              onClick={() => setShowIOSShortcutGuide(true)}
              className="w-full mt-2 text-sm text-[var(--ps-text-muted)] hover:text-[var(--ps-primary)] flex items-center justify-center gap-1"
            >
              Already installed? Learn to share videos <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PWAInstallPrompt;
